import { t } from "@lingui/macro";
import { CloudCheckIcon, SpinnerIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";

/**
 * SaveBadge — the SAVED status pill (Apply Edge builder v3).
 *
 * Lives above the preview (top-left), e.g. passed into RightPanel.statusSlot.
 * Presentational; drive `status` from your auto-save hook.
 */
export type SaveStatus = "saved" | "saving" | "error";

export type SaveBadgeProps = {
  status: SaveStatus;
  onRetry?: () => void;
};

export const SaveBadge = ({ status, onRetry }: SaveBadgeProps) => {
  if (status === "saving") {
    return (
      <span className="flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 font-mono text-[11px] text-muted-foreground">
        <SpinnerIcon size={13} className="animate-spin" />
        {t`Saving…`}
      </span>
    );
  }

  if (status === "error") {
    return (
      <span className="flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-1 font-mono text-[11px] text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">
        <WarningCircleIcon size={13} />
        {t`Save failed`}
        <Button
          variant="ghost"
          size="sm"
          className="h-auto px-1 py-0 text-[11px] underline"
          aria-label={t`Retry`}
          onClick={onRetry}
        >
          {t`Retry`}
        </Button>
      </span>
    );
  }

  return (
    <span
      className={cn(
        "flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 font-mono text-[11px] text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400",
      )}
    >
      <CloudCheckIcon size={13} />
      {t`Saved`}
    </span>
  );
};
